import type { SQLiteDatabase } from 'expo-sqlite';

import { newId, now } from '@/db/ids';
import type { InventoryItemRow, ItemCondition } from '@/db/types';
import { bumpDataRevision } from '@/lib/data-revision';

export const INVENTORY_CATEGORIES = [
  'Electronics',
  'Appliances',
  'Furniture',
  'Jewelry',
  'Art & collectibles',
  'Tools',
  'Outdoor equipment',
  'Sporting goods',
  'Musical instruments',
  'Documents & identity',
  'Luxury / watches',
  'Firearms',
  'Other',
] as const;

export const COMMON_ROOMS = [
  'Living room',
  'Primary bedroom',
  'Bedroom 2',
  'Bedroom 3',
  'Kitchen',
  'Dining room',
  'Primary bath',
  'Bath 2',
  'Office',
  'Garage',
  'Basement',
  'Attic',
  'Shed',
  'Outside',
  'Other',
] as const;

export const CONDITION_OPTIONS: { v: ItemCondition; label: string; tone: string }[] = [
  { v: 'excellent', label: 'Excellent — like new', tone: '#5C7E55' },
  { v: 'good', label: 'Good — minor wear', tone: '#3B5C4A' },
  { v: 'fair', label: 'Fair — usable, dated', tone: '#C2842A' },
  { v: 'poor', label: 'Poor — needs replacement', tone: '#B05F3C' },
];

export interface NewInventoryInput {
  propertyId: string;
  systemId?: string | null;
  room?: string | null;
  name: string;
  category?: string | null;
  brand?: string | null;
  model?: string | null;
  serial_number?: string | null;
  purchase_date?: number | null;
  purchase_cents?: number | null;
  value_cents?: number | null;
  condition?: ItemCondition | null;
  vendor?: string | null;
  warranty_until?: number | null;
  is_insured?: number;
  photo_uri?: string | null;
  extra_photos?: string[] | null;
  photo_captions?: Record<string, string> | null;
  receipt_doc_id?: string | null;
  warranty_doc_id?: string | null;
  notes?: string | null;
}

export function parseExtraPhotos(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Parse the per-photo caption map. Stored as a dict keyed by URI so
 * captions survive when photos are reordered or removed (a parallel
 * array would drift).
 */
export function parsePhotoCaptions(raw: string | null | undefined): Record<string, string> {
  if (!raw) return {};
  try {
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export async function addInventoryItem(
  db: SQLiteDatabase,
  input: NewInventoryInput
): Promise<string> {
  const id = newId();
  const t = now();
  await db.runAsync(
    `INSERT INTO inventory_item (
      id, property_id, system_id, room, name, category, brand, model,
      serial_number, purchase_date, purchase_cents, value_cents, condition,
      vendor, warranty_until, is_insured, photo_uri, extra_photos_json,
      photo_captions_json, receipt_doc_id, warranty_doc_id, notes,
      created_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    id,
    input.propertyId,
    input.systemId ?? null,
    input.room ?? null,
    input.name.trim(),
    input.category ?? null,
    input.brand ?? null,
    input.model ?? null,
    input.serial_number ?? null,
    input.purchase_date ?? null,
    input.purchase_cents ?? null,
    input.value_cents ?? null,
    input.condition ?? null,
    input.vendor ?? null,
    input.warranty_until ?? null,
    input.is_insured ?? 0,
    input.photo_uri ?? null,
    input.extra_photos && input.extra_photos.length
      ? JSON.stringify(input.extra_photos)
      : null,
    input.photo_captions && Object.keys(input.photo_captions).length
      ? JSON.stringify(input.photo_captions)
      : null,
    input.receipt_doc_id ?? null,
    input.warranty_doc_id ?? null,
    input.notes ?? null,
    t,
    t
  );
  bumpDataRevision();
  return id;
}

export interface UpdateInventoryInput
  extends Partial<Omit<NewInventoryInput, 'propertyId' | 'extra_photos' | 'photo_captions'>> {
  extra_photos?: string[] | null;
  photo_captions?: Record<string, string> | null;
}

export async function updateInventoryItem(
  db: SQLiteDatabase,
  id: string,
  input: UpdateInventoryInput
): Promise<void> {
  const t = now();
  const sets: string[] = [];
  const args: (string | number | null)[] = [];
  const fields: (keyof UpdateInventoryInput)[] = [
    'systemId', 'room', 'name', 'category', 'brand', 'model',
    'serial_number', 'purchase_date', 'purchase_cents', 'value_cents',
    'condition', 'vendor', 'warranty_until', 'is_insured', 'photo_uri',
    'extra_photos', 'receipt_doc_id', 'warranty_doc_id', 'notes',
  ];
  const COLUMN_MAP: Partial<Record<keyof UpdateInventoryInput, string>> = {
    systemId: 'system_id',
    extra_photos: 'extra_photos_json',
  };
  for (const f of fields) {
    if (input[f] !== undefined) {
      const col = COLUMN_MAP[f] ?? f;
      if (f === 'extra_photos') {
        const arr = input.extra_photos;
        sets.push(`${col} = ?`);
        args.push(arr && arr.length ? JSON.stringify(arr) : null);
      } else {
        sets.push(`${col} = ?`);
        args.push((input[f] as string | number | null) ?? null);
      }
    }
  }
  if (input.photo_captions !== undefined) {
    sets.push(`photo_captions_json = ?`);
    const obj = input.photo_captions;
    args.push(obj && Object.keys(obj).length ? JSON.stringify(obj) : null);
  }
  if (sets.length === 0) return;
  sets.push('updated_at = ?');
  args.push(t);
  args.push(id);
  await db.runAsync(`UPDATE inventory_item SET ${sets.join(', ')} WHERE id = ?`, ...args);
  bumpDataRevision();
}

export async function deleteInventoryItem(db: SQLiteDatabase, id: string): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE inventory_item SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
  bumpDataRevision();
}

export async function listInventory(
  db: SQLiteDatabase,
  propertyId: string
): Promise<InventoryItemRow[]> {
  return await db.getAllAsync<InventoryItemRow>(
    `SELECT * FROM inventory_item
     WHERE property_id = ? AND deleted_at IS NULL
     ORDER BY created_at DESC`,
    propertyId
  );
}

export async function getInventoryItem(
  db: SQLiteDatabase,
  id: string
): Promise<InventoryItemRow | null> {
  return await db.getFirstAsync<InventoryItemRow>(
    `SELECT * FROM inventory_item WHERE id = ? AND deleted_at IS NULL`,
    id
  );
}

export async function getInventoryTotals(
  db: SQLiteDatabase,
  propertyId: string
): Promise<{ count: number; totalValueCents: number; insuredCount: number }> {
  const row = await db.getFirstAsync<{
    count: number;
    total: number | null;
    insured: number;
  }>(
    `SELECT
       COUNT(*) AS count,
       SUM(COALESCE(value_cents, purchase_cents, 0)) AS total,
       SUM(CASE WHEN is_insured = 1 THEN 1 ELSE 0 END) AS insured
     FROM inventory_item
     WHERE property_id = ? AND deleted_at IS NULL`,
    propertyId
  );
  return {
    count: row?.count ?? 0,
    totalValueCents: row?.total ?? 0,
    insuredCount: row?.insured ?? 0,
  };
}
